import React from "react";
import type { ReactNode } from "react";

interface TooltipErrorBoundaryProps {
  children: ReactNode;
}

interface TooltipErrorBoundaryState {
  hasError: boolean;
}

// A narrow error boundary scoped to a single tooltip's content — `content`/
// `tooltipContent` is caller-supplied and can be arbitrary JSX (a formatted
// date, a computed string, a copy button). A bug in any one of those
// shouldn't take down the table row (or page) it's rendered inside; it
// should just make that one tooltip render nothing. Distinct from the
// app-root `components/ErrorBoundary` (catches everything, renders a full
// error message) — this one is silent and local by design.
class TooltipErrorBoundary extends React.Component<TooltipErrorBoundaryProps, TooltipErrorBoundaryState> {
  constructor(props: TooltipErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    if (import.meta.env.DEV) {
      console.error("Tooltip content failed to render:", error);
    }
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

export default TooltipErrorBoundary;
